import { GoAButton, GoAIconType, GoASpacer } from '@abgov/react-components';
import { useIsDesktop } from '../../utils/isDesktop';
import { REACT_ALBERTA_CA_ACCOUNT_URL } from '../../state/configuration';

export interface WizardButtonGroupProps {
  nextPage: string;
  validate?: () => boolean | Promise<boolean>; // Updated to support async
  save?: () => void | Promise<void>; // Updated to support async
  navigate?: (id: string) => void;
  goToReview?: () => void;
  showViewSummaryButton?: boolean;
  buttonText?: string;
  buttonTrailingIcon?: string;
  disabled?: boolean;
  customButton?: React.ReactNode;
  showVerifyUserButton?: boolean;
}

export const WizardButtonGroup = ({
  nextPage,
  validate: validate,
  save: save,
  navigate: navigate,
  goToReview,
  showViewSummaryButton = true,
  buttonText: buttonText = 'Save and Continue',
  buttonTrailingIcon = '',
  disabled,
  customButton,
  showVerifyUserButton,
}: WizardButtonGroupProps) => {
  const isDesktop = useIsDesktop();

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isDesktop ? 'row' : 'column',
    gap: '15px',
  };

  const handleSaveAndContinue = async () => {
    let valid = true;

    if (validate) {
      // Handle both synchronous and asynchronous validate functions
      valid = await Promise.resolve(validate());
    }

    if (valid) {
      if (save) {
        await Promise.resolve(save()); // Await save if it is async
      }
      if (navigate) {
        navigate(nextPage);
      }
    }
  };

  return (
    <div>
      <GoASpacer vSpacing={'2xl'} />
      <div style={containerStyle}>
        {showVerifyUserButton ? (
          <GoAButton
            disabled={disabled}
            onClick={() => {
              if (REACT_ALBERTA_CA_ACCOUNT_URL) {
                window.open(
                  REACT_ALBERTA_CA_ACCOUNT_URL,
                  '_blank',
                  'noopener,noreferrer',
                );
              }
            }}
            testId="verify-user-button"
            trailingIcon="open"
          >
            Verify your account
          </GoAButton>
        ) : (
          <GoAButton
            testId="save-continue-button"
            trailingIcon={
              buttonTrailingIcon
                ? (buttonTrailingIcon as GoAIconType)
                : undefined
            }
            disabled={disabled}
            onClick={handleSaveAndContinue} // Updated to use async handler
          >
            {buttonText}
          </GoAButton>
        )}
        <div style={{ display: showViewSummaryButton ? 'block' : 'none' }}>
          <GoAButton
            type="tertiary"
            disabled={disabled}
            onClick={() => {
              if (goToReview) goToReview();
            }}
          >
            View report summary
          </GoAButton>
        </div>
        <div>{customButton}</div>
      </div>{' '}
    </div>
  );
};

export const defaultValidate = (
  selection: string,
  defaultSelection: string,
  errorText: string,
  setFormError: (formError: string) => void,
  pushErrorNotification?: (error: string) => void,
  customValidationResult: boolean = true,
): boolean => {
  if (selection == defaultSelection || !customValidationResult) {
    setFormError(errorText);
    if (pushErrorNotification) pushErrorNotification(errorText);
    return false;
  }

  return true;
};
